import { Injectable } from '@angular/core';
import { EMPTY, catchError, mergeMap } from 'rxjs';
import { ServerDataService } from '@core/services/server-data/server-data.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { AccessResponse, Retailer } from '@root/app.interfaces';
import { STORAGE_NAMES } from '@root/app.config';

type EmbeddedStatsPayload = AccessResponse & {
	retailers: Retailer['id'][];
	origin: 'DYB-Embedded';
	search: string;
	search_type: 'postcode';
};

@Injectable({
	providedIn: 'root',
})
export class EmbeddedLinkReportingService {
	private hasTrackedInteraction = false;
	private trackingArmed = false;
	private readonly clickListener = this.handleDocumentClick.bind(this);

	constructor(
		private serverDataService: ServerDataService,
		private sessionStorageService: SessionStorageService,
	) {}

	armFirstEmbeddedInteractionTracking(): void {
		if (this.hasTrackedInteraction || this.trackingArmed || !this.getEmbeddedRetailer()) {
			return;
		}

		document.addEventListener('click', this.clickListener, true);
		this.trackingArmed = true;
	}

	sendEmbeddedEntryReport(): void {
		const embeddedRetailer = this.getEmbeddedRetailer();

		if (this.hasTrackedInteraction || !embeddedRetailer) {
			this.disarmTracking();
			return;
		}

		this.hasTrackedInteraction = true;
		this.disarmTracking();

		this.serverDataService
			.GetAccess()
			.pipe(
				mergeMap(({ access, search_count }) =>
					this.serverDataService.PostStatsData<EmbeddedStatsPayload>({
						retailers: [embeddedRetailer.id],
						origin: 'DYB-Embedded',
						search: '',
						search_type: 'postcode',
						access,
						search_count,
					} satisfies EmbeddedStatsPayload),
				),
				catchError((error) => {
					console.error('Embedded link reporting failed:', error);
					return EMPTY;
				}),
			)
			.subscribe();
	}

	private handleDocumentClick(event: Event): void {
		if (!event.isTrusted) {
			return;
		}

		this.sendEmbeddedEntryReport();
	}

	private disarmTracking(): void {
		if (!this.trackingArmed) {
			return;
		}

		document.removeEventListener('click', this.clickListener, true);
		this.trackingArmed = false;
	}

	private getEmbeddedRetailer(): Retailer | null {
		return this.sessionStorageService.getSession(STORAGE_NAMES.zip_embedded_retailer);
	}
}
